import deserialize from 'app/utils/deserialize'
import get from 'lodash/get'
import posthog from 'app/utils/posthog'
import { fetch, create, remove } from 'app/utils/data'

/**
 * This function takes a {@link Promise} representing a sign in attempt, awaits its response, deserializes the response
 * data, sets analytics data (see {@link https://matteo-gabriele.gitbook.io/vue-gtag/ vue-gtag Get Started},
 * {@link https://developers.google.com/analytics/devguides/collection/gtagjs Google Analytics}, and
 * {@link https://developer.mozilla.org/en-US/docs/Web/API/Beacon_API Beacon API} for details), adds critical data to the
 * Vuex store, and returns the deserialized response.
 *
 * @async
 * @function handleSession
 * @param {Promise<Object>} request - The RESTful sign in request
 * @param {import('vuex').Commit} commit - The Vuex commit object
 * @returns {Object} - The response to the sign in request
 */
async function handleSession(request, commit) {
  try {
    const response = await request

    commit('tokenFetched', { token: response.headers['auth-token'] })

    const session = await deserialize(response.data)

    commit('signedIn', { session })
    commit('accounts/accountFetched', { account: get(session, ['account']) }, { root: true })
    commit('permits/permitsFetched', { permits: get(session, ['account', 'permits']) }, { root: true })
    commit('profiles/profileFetched', { profile: get(session, ['account', 'profile']) }, { root: true })
    commit('kickstarterBackers/kickstarterBackerFetched', { kickstarterBacker: get(session, ['account', 'kickstarterBacker']) }, { root: true })
    commit('licenses/licensesFetched', { licenses: get(session, ['account', 'licenses']) }, { root: true })
    commit('tokens/tokensFetched', { tokens: get(session, ['account', 'tokens']) }, { root: true })
    commit('subscriptions/subscriptionFetched', { subscription: get(session, ['account', 'subscription']) }, { root: true })
    commit('plans/planFetched', { plan: get(session, ['account', 'subscription', 'plan']) }, { root: true })

    identifyUserForBeacon(session)
    identifyUserForPosthog(session)

    return session
  } catch (e) {
    commit('signInFailed')

    throw e
  }
}

function identifyUserForBeacon(session) {
  if (window.Beacon) {
    window.Beacon('identify', {
      name: get(session, ['account', 'profile', 'fullName'], ''),
      email: get(session, ['account', 'email']),
      signature: get(session, ['account', 'beaconSignature']),
      plan: get(session, ['account', 'subscription', 'plan', 'name']),
      kickstarterBacker: get(session, ['account', 'profile', 'kickstarterBacker']),
      preorderBacker: get(session, ['account', 'profile', 'preorderBacker'])
    })
  }
}

function identifyUserForPosthog(session) {
  // Flat keys so PostHog can filter and break down by them.
  posthog.identify(get(session, ['account', 'id']), {
    plan_name: get(session, ['account', 'subscription', 'plan', 'name']),
    plan_sku: get(session, ['account', 'subscription', 'plan', 'sku'])
  })
}

function removeSession(commit, modules) {
  if (window.Beacon) {
    window.Beacon('destroy')
  }

  posthog.reset()

  const skip = ['route']

  modules.forEach((module) => {
    if (!~skip.indexOf(module)) {
      commit(`${module}/resetState`, {}, { root: true })
    }
  })
}

/**
 * A default object containing the Vuex store state, getters, mutations, and actions for the store associated with session
 * management. See {@link https://v3.vuex.vuejs.org/guide/ Vuex Core Concepts} for more details.
 */
export default {
  namespaced: true,

  state: {
    evidenceOfPastSession: false,
    item: null,
    token: null
  },

  recall: ['evidenceOfPastSession'],

  getters: {
    current: (state) => state.item,
    account: (state) => get(state, ['item', 'account'], null),
    isSignedIn: (state) => !!state.item,
    evidenceOfPastSession: (state) => state.evidenceOfPastSession,
    email: (state) => get(state, ['item', 'account', 'email'], null),

    authorizationHeader(state) {
      return { authorization: `Bearer ${state.token}` }
    }
  },

  mutations: {
    signedIn(state, { session }) {
      state.evidenceOfPastSession = true
      state.item = session
    },

    signInFailed(state) {
      state.evidenceOfPastSession = false
      state.item = null
      state.token = null
    },

    tokenFetched(state, { token }) {
      state.token = token
    },

    resetState(state) {
      state.evidenceOfPastSession = false
      state.item = null
      state.token = null
    }
  },

  actions: {
    /**
     * This method takes Vuex commit, state, store, and dispatch objects along with parameters that require an attempt to
     * fetch a current session. The info is used to get a {@link Promise} from fetch (which is the RESTful response to the
     * posted data). That response is then passed to {@link handleSession}.
     *
     * @param {Object} param0 - The Vuex commit, state, store, and dispatch objects
     * @param {import('vuex').Commit} param0.commit - The Vuex commit object
     * @param {import('vuex').state} param0.state - The Vuex state object
     * @param {import('vuex').Store} param0.store - The Vuex store object
     * @param {import('vuex').Dispatch} param0.dispatch - The Vuex dispatch object
     * @param {Object} params - Additional parameters used to determine how to handle fetching the session
     * @returns {Object} - The response to the sign in request
     */
    async fetchCurrentSession({ commit, state, store, dispatch }, params = {}) {
      const { force = false } = params

      if (!state.evidenceOfPastSession && !force) {
        return Promise.reject(new Error('failed'))
      } else {
        // Started together, not in sequence. `handleSession` does not read
        // policies -- it only commits the session, account and permits -- so
        // awaiting the policies fetch before even issuing the session request
        // put two independent round-trips end to end on the critical path of
        // every returning visitor's first paint.
        const policies = dispatch('policies/fetchPolicies', { force: true }, { root: true })

        const session = handleSession(
          fetch({
            url: '/api/sessions/current'
          }),
          commit,
          dispatch
        )

        // A visitor without a session gets a 204 and this rejects while policies
        // are still in flight, before the caller can attach a handler; the browser
        // reports that as an uncaught rejection. The caller still sees it below.
        session.catch(() => {})

        await policies

        return session
      }
    },

    /**
     * This method takes Vuex commit and dispatch objects along with user sign in information and preferences. The user info
     * is used to get a {@link Promise} from create (which is the RESTful response to the posted data). That response is
     * then passed to {@link handleSession}.
     *
     * @param {Object} param0 - The Vuex commit and dispatch objects
     * @param {import('vuex').Commit} param0.commit - The Vuex commit object
     * @param {import('vuex').Dispatch} param0.dispatch - The Vuex dispatch object
     * @param {Object} param1 - The sign in data
     * @param {string} param1.email - The users email address
     * @param {string} param1.password - The users password (at this point in clear text)
     * @param {boolean} param1.rememberMe - The flag indicating the user wants Font Awesome to remember this session
     * @param {boolean} param1.isGdprUser - The flag indicating the user is a user to whom GDPR applies
     * @param {boolean} param1.dpaAccepted - The flag indicating the user has accepted the DPA
     * @returns {Object} - The response to the sign in request
     */
    async signIn({ commit, dispatch }, { email, password, rememberMe, isGdprUser, dpaAccepted, emailPreferencesOptIn = null }) {
      await dispatch('policies/fetchPolicies', { force: true }, { root: true })

      const attributes = {
        email,
        password,
        rememberMe,
        isGdprUser,
        dpaAccepted
      }

      if (emailPreferencesOptIn !== null) {
        attributes.emailPreferencesOptIn = emailPreferencesOptIn
      }

      const session = await handleSession(
        create({
          url: '/api/sessions',
          type: 'session',
          data: attributes
        }),
        commit,
        dispatch
      )

      return session
    },

    signOut({ commit, rootState }) {
      const modules = Object.keys(rootState)

      return new Promise((resolve, reject) => {
        remove({
          url: `/api/sessions/current`
        })
          .then((response) => {
            removeSession(commit, modules)
            resolve()
          })
          .catch((_error) => {
            removeSession(commit, modules)
            resolve()
          })
      })
    },

    resetEverything({ commit, rootState }) {
      removeSession(commit, Object.keys(rootState))
    }
  }
}
